import React, { CSSProperties, FocusEvent, forwardRef, ReactElement, ReactNode, Ref } from 'react';
import {
   DataTypeWithInvalid,
   formatNumber,
   getDecimalSeparator,
   getThousandsSeparator,
   InvalidValue,
   parseDecimal
} from '@unifocus/js-utils';
import { ValidatingInput } from '../validating-input/ValidatingInput';
import { InputRefType } from '../base-input/Input';

export declare type DecimalInputValueType = DataTypeWithInvalid<number>;

/**
 * {@docCategory DecimalInput}
 */
export interface DecimalInputProps {
   /**
    * The name of the input item.
    */
   name?: string;

   /**
    * The value.
    */
   value?: DecimalInputValueType;

   /**
    * The placeholder text for the input item.
    */
   placeholder?: string;

   /**
    * Sets the input item to read-only.
    * @defaultvalue false
    */
   readOnly?: boolean;

   /**
    * Set the input item to disabled.
    * @defaultvalue false
    */
   disabled?: boolean;

   /**
    * Sets the width.
    */
   width?: string | number;

   /**
    * Sets the min width.
    */
   minWidth?: string | number;

   /**
    * Sets the max width.
    */
   maxWidth?: string | number;

   /**
    * Determines if a border should be shown around the component.
    * @defaultvalue true
    */
   border?: boolean;

   /**
    * Determines if the component should have internal padding.
    * @defaultvalue true
    */
   padding?: boolean;

   /**
    * Enable the clear feature.
    * @defaultvalue false
    */
   allowClear?: boolean;

   /**
    * Sets the number of decimals to display.
    * @defaultvalue 2
    */
   numberDecimals?: number;

   /**
    * The prefix element.
    */
   prefix?: ReactNode;

   /**
    * The suffix element.
    */
   suffix?: ReactNode;

   /**
    * The label text displayed before (on the left side of) the input field.
    */
   addonBefore?: ReactNode;

   /**
    * The label text displayed after (on the right side of) the input field.
    */
   addonAfter?: ReactNode;

   /**
    * Sets the popupTrigger.
    */
   popupTrigger?: ReactElement;

   /**
    * Set this to focus on first render
    */
   autoFocus?: boolean;

   /**
    * Sets a className.
    */
   className?: string;

   /**
    * Sets styles.
    */
   style?: CSSProperties;

   /**
    * Callback called when the value is changed.
    * @param value - The value
    */
   onChange?: (value?: DecimalInputValueType) => void;

   /**
    * Callback called when the component loses focus.
    * @param event - The focus event object
    */
   onBlur?: (event: FocusEvent<HTMLInputElement>) => void;

   /**
    * Callback called when the component gains focus.
    * @param event - The focus event object
    */
   onFocus?: (event: FocusEvent<HTMLInputElement>) => void;

   /**
    * Function called when the user presses the enter key.
    */
   onPressEnter?: React.KeyboardEventHandler<HTMLInputElement>;

   /**
    * Function called when the dirty state changes.  The input is considered dirty
    * when the raw string value that is currently typed into the field does not
    * match the last good validated value.
    *
    * @param isDirty - True if the raw string value that is currently typed into the field does not match the last good validated value
    */
   onDirtyStateChange?: (isDirty: boolean) => void;
}

export const DecimalInput = forwardRef<InputRefType, DecimalInputProps>(
   (props: DecimalInputProps, ref: Ref<InputRefType>) => {
      return (
         <ValidatingInput<number>
            ref={ref}
            name={props.name}
            type={'text'}
            inputMode={'decimal'}
            placeholder={props.placeholder}
            readOnly={props.readOnly}
            disabled={props.disabled}
            width={props.width}
            minWidth={props.minWidth}
            maxWidth={props.maxWidth}
            border={props.border}
            padding={props.padding}
            prefix={props.prefix}
            suffix={props.suffix}
            addonBefore={props.addonBefore}
            addonAfter={props.addonAfter}
            popupTrigger={props.popupTrigger}
            format={decimalInputFormatter(props.numberDecimals)}
            parse={decimalInputParser()}
            inputValidator={decimalInputValidator(props.numberDecimals)}
            className={props.className}
            style={props.style}
            value={props.value}
            autoFocus={props.autoFocus}
            onChange={props.onChange}
            onBlur={props.onBlur}
            onFocus={props.onFocus}
            onPressEnter={props.onPressEnter}
            onDirtyStateChange={props.onDirtyStateChange}
         />
      );
   });

export const decimalInputFormatter = (numberDecimals?: number) => (value?: number): string => {
   if (value === undefined || isNaN(value)) {
      return '';
   }

   return formatNumber(value, numberDecimals);
};

export const decimalInputParser = () => (value?: string): DecimalInputValueType => {
   if (value === undefined || value === null || value.trim().length === 0) {
      return undefined;
   }

   const parseResult = parseDecimal(value);

   return isNaN(parseResult) ? InvalidValue : parseResult;
};

export const decimalInputValidator = (numberDecimals?: number) => {
   const decimalPlacesRegex = numberDecimals ? `{0,${numberDecimals}}` : '*';
   const thousandsSeparator = getThousandsSeparator();
   const decimalSeparator = getDecimalSeparator();
   const regExp = RegExp(`^[-]?[\\d\\${thousandsSeparator}]*?[\\${decimalSeparator}]?\\d${decimalPlacesRegex}$`,
      'i');

   return (value?: string): boolean => {
      if (value) {
         return regExp.test(value);
      }

      return true;
   };
};
